<template>
  <section class="px-10 pt-8 pb-16">
    <div class="flex items-center justify-between mb-5">
      <h2
        class="font-display text-[11px] font-semibold tracking-widest uppercase flex-shrink-0"
        :class="isLight ? 'text-[#0f1f1c]' : 'text-white'"
      >
        {{ $t('map.title') }}
        <span
          class="text-xs font-normal ml-2"
          :class="isLight ? 'text-[#6b8f89]' : 'text-[#a8d5cc]/60'"
        >
          {{ $t('map.nearYou') }}
        </span>
      </h2>
    </div>

    <div
      class="rounded-2xl overflow-hidden border h-[420px]"
      :class="
        isLight
          ? 'border-[#d0ede8] shadow-md shadow-[#1a9b8a]/10'
          : 'border-[#1a9b8a]/20 shadow-lg shadow-black/30'
      "
    >
      <div id="map" ref="mapEl" class="w-full h-full"></div>
    </div>

    <MapEventModelComponent
      :showModal="showModal"
      :selectedEvent="selectedEvent"
      :isLight="isLight"
      @close="closeModal"
    />
  </section>
</template>

<script>
import L from "leaflet";
import { toRaw } from "vue";
import MapEventModelComponent from "./MapEventModelComponent.vue";

export default {
	name: "MapComponent",
	components: { MapEventModelComponent },
	props: {
		searchTerm: String,
		isLight: Boolean,
		events: { type: Array, default: () => [] },
	},
	emits: ["viewport-change"],
	data() {
		return {
			map: null, userPosition: null, currentZoom: 13,
			fetchRadius: 5000, isZooming: false,
			showModal: false, selectedEvent: null,
			markers: [], userMarker: null, renderTimeout: null,
		};
	},
	watch: {
		events() { this.safeRenderMarkers(); },
		isLight() {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }

        this.$nextTick(() => {
            this.initMap();
        });
    },
	},
	mounted() { this.initMap(); },
	beforeUnmount() {
		if (this.renderTimeout) clearTimeout(this.renderTimeout);
		if (toRaw(this.map)) { toRaw(this.map).off(); toRaw(this.map).remove(); }
	},
	methods: {
		initMap() {
			const cartoKey = process.env.VUE_APP_CARTON_KEY;
			console.log(
        "CARTO API Key:",cartoKey ? "Loaded" : "Missing"
    );
			const defaultLocation = [41.9028, 12.4964];
			this.map = toRaw(L.map(this.$refs.mapEl, { minZoom: 1, maxZoom: 18, zoomAnimation: true, fadeAnimation: true, markerZoomAnimation: true })).setView(defaultLocation, 13);
			const tileUrl = this.isLight
				? "https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=" + cartoKey
				: "https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=" + cartoKey;
			L.tileLayer(tileUrl, { attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>', subdomains: "abcd", maxZoom: 20 }).addTo(toRaw(this.map));
			this.setupUserLocation();
			this.setupMapEvents();
			this.renderMarkers();
		},
		setupUserLocation() {
			const userIcon = L.divIcon({
				html: `<div style="width:12px;height:12px;border-radius:50%;background:#1a9b8a;border:2px solid rgba(26,155,138,0.4);box-shadow:0 0 0 5px rgba(26,155,138,0.15);"></div>`,
				className: "", iconSize: [12, 12], iconAnchor: [6, 6],
			});
			if (navigator.geolocation) {
				navigator.geolocation.getCurrentPosition(
					(position) => {
						this.userPosition = [position.coords.latitude, position.coords.longitude];
						if (!toRaw(this.map)) return;
						toRaw(this.map).setView(this.userPosition, 13);
						this.userMarker = L.marker(this.userPosition, { icon: userIcon })
							.addTo(toRaw(this.map))
							.bindPopup(`<span style="color:#1a9b8a;font-weight:700;">${this.$t('map.youAreHere')}</span>`);
						this.emitViewportChange();
					},
					() => { this.emitViewportChange(); }
				);
			} else {
				this.emitViewportChange();
			}
		},
		setupMapEvents() {
			let debounceTimer;
			toRaw(this.map).on("zoomstart", () => { this.isZooming = true; if (toRaw(this.map)) toRaw(this.map).closePopup(); });
			toRaw(this.map).on("zoomend", () => {
				this.isZooming = false;
				clearTimeout(debounceTimer);
				debounceTimer = setTimeout(() => { this.onViewportChange(); this.$nextTick(() => { this.renderMarkers(); }); }, 300);
			});
			toRaw(this.map).on("moveend", () => {
				if (this.isZooming) return;
				clearTimeout(debounceTimer);
				debounceTimer = setTimeout(() => { this.onViewportChange(); }, 300);
			});
		},
		safeRenderMarkers() {
			if (this.renderTimeout) clearTimeout(this.renderTimeout);
			this.renderTimeout = setTimeout(() => { if (!this.isZooming) this.renderMarkers(); }, 100);
		},
		onViewportChange() {
			if (!toRaw(this.map)) return;
			const zoom = toRaw(this.map).getZoom();
			this.currentZoom = zoom;
			this.fetchRadius = this.calculateFetchRadius(zoom);
			this.emitViewportChange();
		},
		emitViewportChange() {
			if (!toRaw(this.map)) return;
			const center = toRaw(this.map).getCenter();
			this.$emit("viewport-change", { latitude: center.lat, longitude: center.lng, radius: this.fetchRadius, zoom: this.currentZoom });
		},
		calculateFetchRadius(zoomLevel) {
			if (zoomLevel <= 10) return 10000;
			if (zoomLevel <= 12) return 5000;
			if (zoomLevel <= 14) return 2000;
			return 1000;
		},
		buildPinSvg(imageUrl) {
			return `<svg width="44" height="44" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg"><defs><clipPath id="clip-c"><circle cx="22" cy="22" r="19"/></clipPath><filter id="pin-shadow" x="-40%" y="-40%" width="180%" height="180%"><feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="#1a9b8a" flood-opacity="0.45"/></filter></defs><circle cx="22" cy="22" r="22" fill="#1a9b8a" filter="url(#pin-shadow)"/><circle cx="22" cy="22" r="19" fill="#0d6b5e"/>${imageUrl ? `<image href="${imageUrl}" x="3" y="3" width="38" height="38" clip-path="url(#clip-c)" preserveAspectRatio="xMidYMid slice"/>` : `<circle cx="22" cy="22" r="7" fill="white"/>`}<circle cx="22" cy="22" r="19" fill="none" stroke="white" stroke-width="2"/></svg>`;
		},
		buildPopupHtml(event) {
			const bg = this.isLight ? "#ffffff" : "#132420";
			const textColor = this.isLight ? "#0f1f1c" : "#ffffff";
			const subColor = this.isLight ? "#0d6b5e" : "#a8d5cc";
			const imageUrl = event.imageUrl || event.ImageURL || "";
			const title = event.title || event.Title || "Untitled Event";
			const venue = event.location_name || event.venue || event.location || "";
			const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}&travelmode=driving`;
			const directionsLabel = this.$t('map.getDirections');
			return `<div style="font-family:Inter,sans-serif;background:${bg};border-radius:16px;padding:14px;min-width:160px;max-width:210px;text-align:center;">${imageUrl ? `<img src="${imageUrl}" style="width:64px;height:64px;border-radius:50%;object-fit:cover;margin:0 auto 10px;display:block;border:2.5px solid #1a9b8a;"/>` : ""}<p style="font-size:12px;font-weight:700;color:${textColor};margin:0 0 4px;line-height:1.3;font-family:'Montserrat',sans-serif;">${title}</p>${venue ? `<p style="font-size:10px;color:${subColor};margin:0 0 10px;line-height:1.4;">${venue}</p>` : ""}<a href="${directionsUrl}" target="_blank" style="display:inline-flex;align-items:center;gap:5px;background:#1a9b8a;color:#fff;font-size:10px;font-weight:700;padding:6px 12px;border-radius:20px;text-decoration:none;letter-spacing:.04em;">${directionsLabel}</a></div>`;
		},
		renderMarkers() {
			if (!toRaw(this.map) || this.isZooming) return;
			this.markers.forEach((marker) => {
				try { if (toRaw(this.map) && toRaw(this.map).hasLayer(marker)) { marker.closePopup(); toRaw(this.map).removeLayer(marker); } } catch (err) { console.warn("Marker removal skipped:", err); }
			});
			this.markers = [];
			this.events.forEach((event) => {
				if (!event.latitude || !event.longitude) return;
				const imageUrl = event.imageUrl || event.ImageURL || "";
				const icon = L.divIcon({ html: this.buildPinSvg(imageUrl), className: "", iconSize: [44, 44], iconAnchor: [22, 22], popupAnchor: [0, -26] });
				const marker = L.marker([event.latitude, event.longitude], { icon });
				marker.bindPopup(this.buildPopupHtml(event), { className: "trekko-popup", maxWidth: 220 });
				marker.on("popupopen", () => {
					const popupDiv = document.querySelector(".leaflet-popup-content");
					if (popupDiv) { popupDiv.style.cursor = "pointer"; popupDiv.onclick = () => { this.selectedEvent = event; this.showModal = true; }; }
				});
				if (toRaw(this.map)) marker.addTo(toRaw(this.map));
				this.markers.push(marker);
			});
		},
		closeModal() { this.showModal = false; this.selectedEvent = null; },
	},
};
</script>

<style>
.leaflet-popup-content-wrapper { background: transparent !important; border: none !important; box-shadow: 0 12px 40px rgba(0,0,0,0.3) !important; border-radius: 16px !important; padding: 0 !important; }
.leaflet-popup-content { margin: 0 !important; }
.leaflet-popup-tip-container { display: none !important; }
.trekko-popup b { color: #1a9b8a; font-family: "Montserrat", sans-serif; }
</style>